import React, { useState, useEffect, useCallback } from 'react';
import { shippingApi, ordersApi } from '../services/api';
import { OrderDetailsModal } from '../components/OrderDetailsModal';
import { MapPinIcon, GlobeAltIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { formatDate } from '../utils/dateUtils';

interface Package {
  id: string;
  tracking_number: string;
  package_number: number;
  weight: number;
  label_data?: string;
}

interface Shipment {
  id: string;
  order_id: string;
  order_number: string;
  first_name?: string;
  last_name?: string;
  customer_email?: string;
  carrier: string;
  service_level: string;
  tracking_number: string;
  cost: number;
  status: string;
  is_international: boolean;
  created_at: string;
  updated_at: string;
  shipping_city?: string;
  shipping_state?: string;
  shipping_country?: string;
  order_total?: number;
  packages: Package[];
}

// Helper to detect label format from base64 data
const detectLabelFormat = (base64: string): { contentType: string; extension: string } => {
  if (base64.startsWith('R0lGOD')) {
    return { contentType: 'image/gif', extension: 'gif' };
  } else if (base64.startsWith('iVBORw')) {
    return { contentType: 'image/png', extension: 'png' };
  } else if (base64.startsWith('JVBERi')) {
    return { contentType: 'application/pdf', extension: 'pdf' };
  }
  return { contentType: 'application/pdf', extension: 'pdf' };
};

// Helper to convert base64 to Blob
const base64ToBlob = (base64: string, contentType: string = 'application/pdf'): Blob => {
  const byteCharacters = atob(base64);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  return new Blob(byteArrays, { type: contentType });
};

// Carrier logo component
const CarrierLogo: React.FC<{ carrier: string }> = ({ carrier }) => {
  const carrierLower = carrier?.toLowerCase() || '';
  
  if (carrierLower.includes('ups')) {
    return (
      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center font-bold text-amber-800 text-xs">
        UPS
      </div>
    );
  } else if (carrierLower.includes('usps')) {
    return (
      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center font-bold text-blue-800 text-xs">
        USPS
      </div>
    );
  } else if (carrierLower.includes('fedex')) {
    return (
      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center font-bold text-purple-800 text-xs">
        FDX
      </div>
    );
  } else if (carrierLower.includes('pickup')) {
    return (
      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-800">
        <MapPinIcon className="w-5 h-5" />
      </div>
    );
  }
  return (
    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center font-bold text-slate-600 text-xs">
      {carrier?.substring(0, 3).toUpperCase() || '?'}
    </div>
  );
};

export const Shipments: React.FC = () => {
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFilter, setDateFilter] = useState<'today' | '7days' | '30days' | 'all'>('30days');
  const [carrierFilter, setCarrierFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [initialTab, setInitialTab] = useState<'summary' | 'ship' | 'shipments' | 'history'>('shipments');
  const [selectedShipments, setSelectedShipments] = useState<Set<string>>(new Set());
  const [isPrinting, setIsPrinting] = useState(false);
  const [total, setTotal] = useState(0);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [page, setPage] = useState(1);
  const shipmentsPerPage = 50;

  const loadShipments = useCallback(async () => {
    setLoading(true);
    try {
      // Calculate date range
      let dateFrom: string | undefined;
      const now = new Date();
      
      if (dateFilter === 'today') {
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        dateFrom = today.toISOString();
      } else if (dateFilter === '7days') {
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        dateFrom = sevenDaysAgo.toISOString();
      } else if (dateFilter === '30days') {
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        dateFrom = thirtyDaysAgo.toISOString();
      }

      const params: any = {
        limit: shipmentsPerPage,
        offset: (page - 1) * shipmentsPerPage,
      };

      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      if (dateFrom) {
        params.date_from = dateFrom;
      }
      if (carrierFilter !== 'all') {
        params.carrier = carrierFilter;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }

      const result = await shippingApi.listShipments(params);
      setShipments(result.shipments || []);
      setTotal(result.total || 0);
    } catch (error) {
      console.error('Failed to load shipments:', error);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, dateFilter, carrierFilter, searchQuery, page]);

  useEffect(() => {
    loadShipments();
  }, [loadShipments]);

  // Search with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page !== 1) {
        setPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Get unique carriers for filter dropdown (always include Pickup option)
  const carriersFromData = Array.from(new Set(shipments.map(s => s.carrier?.toLowerCase()))).filter(Boolean);
  const uniqueCarriers = carriersFromData.includes('local pickup') 
    ? carriersFromData 
    : [...carriersFromData, 'local pickup'];

  const handleSelectAll = () => {
    if (selectedShipments.size === shipments.length) {
      setSelectedShipments(new Set());
    } else {
      setSelectedShipments(new Set(shipments.map(s => s.id)));
    }
  };

  const handleSelectShipment = (shipmentId: string) => {
    const newSelected = new Set(selectedShipments);
    if (newSelected.has(shipmentId)) {
      newSelected.delete(shipmentId);
    } else {
      newSelected.add(shipmentId);
    }
    setSelectedShipments(newSelected);
  };

  const handleBatchPrint = async () => {
    const selectedList = shipments.filter(s => selectedShipments.has(s.id));
    
    if (selectedList.length === 0) {
      alert('Please select shipments to print');
      return;
    }

    // Check if all selected shipments have label data
    const shipmentsWithLabels = selectedList.filter(s => 
      s.packages?.some(p => p.label_data)
    );

    if (shipmentsWithLabels.length === 0) {
      alert('No labels available for selected shipments');
      return;
    }

    setIsPrinting(true);

    try {
      // Collect all label PDFs
      const labelBlobs: Blob[] = [];
      
      for (const shipment of shipmentsWithLabels) {
        for (const pkg of shipment.packages || []) {
          if (pkg.label_data) {
            const format = detectLabelFormat(pkg.label_data);
            const blob = base64ToBlob(pkg.label_data, format.contentType);
            labelBlobs.push(blob);
          }
        }
      }

      if (labelBlobs.length === 0) {
        alert('No labels found to print');
        return;
      }

      // For single label, open in new window
      if (labelBlobs.length === 1) {
        const url = URL.createObjectURL(labelBlobs[0]);
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.addEventListener('load', () => {
            printWindow.print();
          });
        }
      } else {
        // For multiple labels, download as individual files (browser can't merge PDFs)
        // In a real app, you'd use a PDF library to merge them server-side
        for (let i = 0; i < shipmentsWithLabels.length; i++) {
          const shipment = shipmentsWithLabels[i];
          for (const pkg of shipment.packages || []) {
            if (pkg.label_data) {
              const format = detectLabelFormat(pkg.label_data);
              const blob = base64ToBlob(pkg.label_data, format.contentType);
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `label-${/^[0-9]+$/.test(shipment.order_number) ? `PSCS${shipment.order_number}` : shipment.order_number}-${pkg.tracking_number}.${format.extension}`;
              link.click();
              URL.revokeObjectURL(url);
            }
          }
        }
        alert(`Downloaded ${labelBlobs.length} label(s). Open each file to print.`);
      }
    } catch (error) {
      console.error('Batch print error:', error);
      alert('Failed to print labels');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleExportLabels = async () => {
    const selectedList = shipments.filter(s => selectedShipments.has(s.id));
    
    if (selectedList.length === 0) {
      alert('Please select shipments to export');
      return;
    }

    try {
      let exportedCount = 0;
      
      for (const shipment of selectedList) {
        for (const pkg of shipment.packages || []) {
          if (pkg.label_data) {
            const format = detectLabelFormat(pkg.label_data);
            const blob = base64ToBlob(pkg.label_data, format.contentType);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `label-${/^[0-9]+$/.test(shipment.order_number) ? `PSCS${shipment.order_number}` : shipment.order_number}-${pkg.tracking_number}.${format.extension}`;
            link.click();
            URL.revokeObjectURL(url);
            exportedCount++;
          }
        }
      }

      if (exportedCount === 0) {
        alert('No labels available for selected shipments');
      } else {
        alert(`Exported ${exportedCount} label(s)`);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export labels');
    }
  };

  const handleViewOrder = async (orderId: string, tab: 'summary' | 'ship' | 'shipments' | 'history' = 'shipments') => {
    try {
      const orderDetails = await ordersApi.get(orderId);
      setSelectedOrder(orderDetails);
      // For pickup orders, default to ship tab (pickup UI lives there)
      if (orderDetails.order?.order_type === 'pickup') {
        setInitialTab('ship');
      } else {
        setInitialTab(tab);
      }
    } catch (error) {
      console.error('Failed to load order details:', error);
      alert('Failed to load order details');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Shipped</span>;
      case 'pending':
        return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Pending</span>;
      case 'voided':
        return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Voided</span>;
      case 'in_transit':
        return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">In Transit</span>;
      case 'delivered':
        return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Delivered</span>;
      case 'ready_for_pickup':
        return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Ready for Pickup</span>;
      case 'picked_up':
        return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Picked Up</span>;
      default:
        return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">{status}</span>;
    }
  };

  return (
    <div className="absolute inset-0 flex flex-col overflow-y-auto lg:overflow-hidden p-4 lg:p-6">
      {/* Filters - Compact on mobile */}
      <div className="flex-shrink-0 bg-white rounded-xl border border-slate-200 shadow-subtle p-3 lg:p-4 mb-4">
        {/* Mobile: Compact search + filter button */}
        <div className="flex gap-2 lg:hidden">
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
          />
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className={`px-3 py-2 rounded-lg border ${showMobileFilters ? 'bg-brand-blue text-white border-brand-blue' : 'bg-white text-slate-600 border-slate-200'}`}
          >
            <FunnelIcon className="w-5 h-5" />
          </button>
          <button 
            onClick={handleBatchPrint}
            disabled={selectedShipments.size === 0 || isPrinting}
            className="px-3 py-2 text-sm font-medium text-white bg-brand-blue rounded-lg disabled:opacity-50"
          >
            {isPrinting ? '...' : 'Print'}
          </button>
        </div>

        {/* Mobile: Expandable filters */}
        {showMobileFilters && (
          <div className="lg:hidden mt-3 pt-3 border-t border-slate-200 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as 'today' | '7days' | '30days' | 'all')}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="today">Today</option>
                <option value="7days">7 Days</option>
                <option value="30days">30 Days</option>
                <option value="all">All</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="all">All Status</option>
                <option value="confirmed">Shipped</option>
                <option value="pending">Pending</option>
                <option value="voided">Voided</option>
              </select>
              <select
                value={carrierFilter}
                onChange={(e) => setCarrierFilter(e.target.value)}
                className="col-span-2 px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="all">All Carriers</option>
                {uniqueCarriers.map(carrier => (
                  <option key={carrier} value={carrier}>{carrier?.toUpperCase()}</option>
                ))}
              </select>
            </div>
            {selectedShipments.size > 0 && (
              <div className="flex gap-2">
                <span className="text-sm text-slate-600 py-2">{selectedShipments.size} selected</span>
                <button 
                  onClick={handleExportLabels}
                  className="flex-1 px-3 py-2 text-sm font-medium text-brand-blue bg-blue-50 rounded-lg"
                >
                  Export
                </button>
              </div>
            )}
          </div>
        )}

        {/* Desktop: Full filter bar */}
        <div className="hidden lg:flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              placeholder="Search orders, tracking..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue w-40 xl:w-48"
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 pr-8 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="confirmed">Shipped</option>
              <option value="pending">Pending</option>
              <option value="voided">Voided</option>
            </select>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value as 'today' | '7days' | '30days' | 'all')}
              className="px-3 pr-8 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="today">Today</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="all">All Time</option>
            </select>
            <select
              value={carrierFilter}
              onChange={(e) => setCarrierFilter(e.target.value)}
              className="px-3 pr-8 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="all">All Carriers</option>
              {uniqueCarriers.map(carrier => (
                <option key={carrier} value={carrier}>{carrier?.toUpperCase()}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {selectedShipments.size > 0 && (
              <span className="text-sm text-slate-600 hidden xl:inline">{selectedShipments.size} selected</span>
            )}
            <button 
              onClick={handleExportLabels}
              disabled={selectedShipments.size === 0}
              className="px-3 py-2 text-sm font-medium text-brand-blue bg-blue-50 hover:bg-blue-100 rounded-lg disabled:opacity-50 whitespace-nowrap"
            >
              Export Labels
            </button>
            <button 
              onClick={handleBatchPrint}
              disabled={selectedShipments.size === 0 || isPrinting}
              className="px-3 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg disabled:opacity-50 whitespace-nowrap"
            >
              {isPrinting ? 'Printing...' : 'Print Labels'}
            </button>
          </div>
        </div>
      </div>

      {/* Shipments Table */}
      <div className="flex-1 min-h-0 bg-white rounded-xl border border-slate-200 shadow-subtle overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-3 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedShipments.size === shipments.length && shipments.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                  />
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Carrier / Service
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Tracking
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Cost
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {loading && (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-slate-600">
                    Loading shipments...
                  </td>
                </tr>
              )}

              {!loading && shipments.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="text-slate-400 mb-2">
                      <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No shipments found</h3>
                    <p className="text-slate-600">
                      {filterStatus !== 'all' || carrierFilter !== 'all' 
                        ? 'Try adjusting your filters'
                        : 'Ship your first order to see shipments here'}
                    </p>
                  </td>
                </tr>
              )}

              {!loading && shipments.map((shipment) => (
                <tr
                  key={shipment.id}
                  className="hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => handleViewOrder(shipment.order_id)}
                >
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedShipments.has(shipment.id)}
                      onChange={() => handleSelectShipment(shipment.id)}
                      className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                    />
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="text-sm font-mono font-medium text-slate-900">
                      {/^[0-9]+$/.test(shipment.order_number) ? `#PSCS${shipment.order_number}` : shipment.order_number}
                    </span>
                    {shipment.is_international && (
                      <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                        Intl
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div>
                      <p className="text-sm text-slate-900">
                        {shipment.first_name} {shipment.last_name}
                      </p>
                      {shipment.shipping_city && (
                        <p className="text-xs text-slate-500">
                          {shipment.shipping_city}, {shipment.shipping_state}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <CarrierLogo carrier={shipment.carrier} />
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {shipment.carrier?.toUpperCase()}
                        </p>
                        <p className="text-xs text-slate-500 truncate max-w-[120px]">
                          {shipment.service_level}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <p className="text-sm font-mono text-slate-700 truncate max-w-[160px]">
                      {shipment.tracking_number || shipment.packages?.[0]?.tracking_number || '-'}
                    </p>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="text-sm font-medium text-slate-900">
                      ${parseFloat(shipment.cost?.toString() || '0').toFixed(2)}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {getStatusBadge(shipment.status)}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleViewOrder(shipment.order_id, 'shipments')}
                      className="px-2 py-1 text-xs font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded transition-colors"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination - Sticky Footer */}
        {!loading && shipments.length > 0 && (
          <div className="flex-shrink-0 bg-white border-t border-slate-200 px-6 py-3 flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Showing <span className="font-medium">{(page - 1) * shipmentsPerPage + 1}</span> to{' '}
              <span className="font-medium">{Math.min(page * shipmentsPerPage, total)}</span> of{' '}
              <span className="font-medium">{total}</span> shipments
            </p>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-600 mr-2">
                Page {page} of {Math.ceil(total / shipmentsPerPage)}
              </span>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= Math.ceil(total / shipmentsPerPage)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <OrderDetailsModal
          key={`modal-${selectedOrder.order?.id}`}
          order={{ ...selectedOrder.order, items: selectedOrder.items }}
          onClose={() => setSelectedOrder(null)}
          onShip={() => {
            setSelectedOrder(null);
            loadShipments();
          }}
          initialTab={initialTab}
        />
      )}
    </div>
  );
};
